/**
 * Atlas AI API Integration
 *
 * This module provides integration with the Atlas AI platform API
 * for managing AI-powered calls, conversations, and customer engagement.
 *
 * Setup:
 * 1. Get your API key from Atlas: App Configuration > Data > API
 * 2. Add to Vercel environment variables (server-side, no REACT_APP_ prefix):
 *    ATLAS_API_KEY=your_api_key_here
 *
 * Atlas API Documentation: https://developers.atlas.so/
 */

import { getAuthHeaders } from 'src/utils/auth-headers';

// ============================================
// Types
// ============================================

export interface AtlasCall {
  id: string;
  phoneNumber: string;
  customerName: string | null;
  status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'no_answer';
  direction: 'inbound' | 'outbound';
  duration: number | null;
  startedAt: string | null;
  endedAt: string | null;
  createdAt: string;
  recordingUrl: string | null;
  transcriptUrl: string | null;
  summary: string | null;
  sentiment: 'positive' | 'neutral' | 'negative' | null;
  outcome: string | null;
  campaignId: string | null;
  campaignName: string | null;
  agentId: string | null;
  metadata: Record<string, unknown>;
}

export interface AtlasConversation {
  id: string;
  callId: string;
  messages: AtlasMessage[];
  createdAt: string;
  updatedAt: string;
}

export interface AtlasMessage {
  id: string;
  role: 'agent' | 'customer' | 'system';
  content: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

export interface AtlasCampaign {
  id: string;
  name: string;
  description: string | null;
  status: 'active' | 'paused' | 'completed' | 'draft';
  type: 'outbound' | 'inbound';
  totalCalls: number;
  completedCalls: number;
  successfulCalls: number;
  averageDuration: number;
  createdAt: string;
  updatedAt: string;
  agentId: string;
  schedule: AtlasSchedule | null;
}

export interface AtlasSchedule {
  timezone: string;
  startTime: string;
  endTime: string;
  daysOfWeek: number[];
  startDate: string | null;
  endDate: string | null;
}

export interface AtlasAgent {
  id: string;
  name: string;
  description: string | null;
  voiceId: string;
  language: string;
  greeting: string;
  systemPrompt: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AtlasAction {
  id: string;
  name: string;
  description: string | null;
  type: 'webhook' | 'api_call' | 'data_lookup';
  endpoint: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  headers: Record<string, string>;
  isActive: boolean;
  createdAt: string;
}

export interface AtlasWebhookEvent {
  id: string;
  type: 'call.started' | 'call.ended' | 'call.failed' | 'conversation.updated' | 'action.triggered';
  payload: Record<string, unknown>;
  createdAt: string;
  status: 'pending' | 'delivered' | 'failed';
}

export interface AtlasStats {
  totalCalls: number;
  completedCalls: number;
  averageDuration: number;
  successRate: number;
  callsToday: number;
  callsThisWeek: number;
  callsThisMonth: number;
  topOutcomes: { outcome: string; count: number }[];
  sentimentBreakdown: {
    positive: number;
    neutral: number;
    negative: number;
  };
}

// ============================================
// Constants
// ============================================

// Use local proxy to avoid CORS issues
const ATLAS_API_PROXY = '/api/proxy/atlas';

// ============================================
// Configuration
// ============================================

// API key is handled server-side via proxy
const getApiKey = (): string | null => {
  return null; // Only available server-side
};

/**
 * Check if Atlas API is properly configured
 * Always returns true - actual check happens server-side
 */
export const isAtlasConfigured = (): boolean => {
  return true; // Server-side proxy validates credentials
};

/**
 * Get configuration status with details
 */
export const getAtlasConfigStatus = (): {
  configured: boolean;
  hasApiKey: boolean;
} => {
  return {
    configured: isAtlasConfigured(),
    hasApiKey: Boolean(getApiKey()),
  };
};

// ============================================
// API Helper
// ============================================

const atlasRequest = async <T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> => {
  // Use local proxy to avoid CORS - the proxy adds the API key server-side
  const url = `${ATLAS_API_PROXY}?endpoint=${encodeURIComponent(endpoint)}`;

  console.log('[Atlas API] Requesting:', endpoint);

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
      ...options.headers,
    },
  });

  const responseText = await response.text();
  console.log('[Atlas API] Response status:', response.status, 'for', endpoint);
  console.log('[Atlas API] Response body:', responseText.substring(0, 500));

  if (!response.ok) {
    let errorMessage: string;

    try {
      const errorJson = JSON.parse(responseText);
      errorMessage = errorJson.message || errorJson.error || `HTTP ${response.status}`;
    } catch {
      errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    }

    throw new Error(errorMessage);
  }

  try {
    return JSON.parse(responseText);
  } catch {
    console.error('[Atlas API] Failed to parse response as JSON');
    throw new Error('Invalid JSON response from Atlas API');
  }
};

// ============================================
// Calls API
// ============================================

/**
 * Get call records/statistics
 */
export const getCalls = async (params?: {
  campaignId?: string;
  limit?: number;
  offset?: number;
}): Promise<{ calls: AtlasCall[]; total: number }> => {
  if (!isAtlasConfigured()) {
    console.warn('Atlas not configured');
    return { calls: [], total: 0 };
  }

  try {
    // Atlas uses /statistics/call-records endpoint
    const queryParams = new URLSearchParams();
    if (params?.campaignId) queryParams.set('campaignId', params.campaignId);
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    if (params?.offset) queryParams.set('offset', params.offset.toString());
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';

    const response = await atlasRequest<unknown>(`/statistics/call-records${queryString}`);

    // Handle different response formats
    if (Array.isArray(response)) {
      return { calls: response as AtlasCall[], total: response.length };
    }
    const data = response as { data?: AtlasCall[]; calls?: AtlasCall[]; records?: AtlasCall[]; items?: AtlasCall[]; total?: number };
    const calls = data.data || data.calls || data.records || data.items || [];
    return { calls, total: data.total || calls.length };
  } catch (error) {
    console.error('Failed to fetch Atlas calls:', error);
    throw error;
  }
};

/**
 * Get a single call by ID
 */
export const getCall = async (callId: string): Promise<AtlasCall | null> => {
  if (!isAtlasConfigured()) {
    return null;
  }

  try {
    const response = await atlasRequest<{ call: AtlasCall }>(`/calls/${callId}`);
    return response.call;
  } catch (error) {
    console.error(`Failed to fetch call ${callId}:`, error);
    return null;
  }
};

/**
 * Schedule a new call
 */
export const scheduleCall = async (data: {
  phoneNumber: string;
  customerName?: string;
  campaignId?: string;
  scheduledAt?: string;
  metadata?: Record<string, unknown>;
}): Promise<AtlasCall | null> => {
  if (!isAtlasConfigured()) {
    return null;
  }

  try {
    // Atlas uses /calls/scheduled endpoint for creating scheduled calls
    const response = await atlasRequest<{ data: AtlasCall } | AtlasCall>('/calls/scheduled', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if ('data' in response) {
      return response.data;
    }
    return response;
  } catch (error) {
    console.error('Failed to schedule call:', error);
    throw error;
  }
};

/**
 * Cancel a scheduled call
 */
export const cancelCall = async (callId: string): Promise<boolean> => {
  if (!isAtlasConfigured()) {
    return false;
  }

  try {
    await atlasRequest(`/calls/${callId}/cancel`, {
      method: 'POST',
    });
    return true;
  } catch (error) {
    console.error(`Failed to cancel call ${callId}:`, error);
    return false;
  }
};

// ============================================
// Conversations API
// ============================================

/**
 * Get conversation for a call
 */
export const getConversation = async (callId: string): Promise<AtlasConversation | null> => {
  if (!isAtlasConfigured()) {
    return null;
  }

  try {
    const response = await atlasRequest<{ conversation: AtlasConversation }>(
      `/calls/${callId}/conversation`
    );
    return response.conversation;
  } catch (error) {
    console.error(`Failed to fetch conversation for call ${callId}:`, error);
    return null;
  }
};

// ============================================
// Campaigns API
// ============================================

/**
 * Get all campaigns
 */
export const getCampaigns = async (): Promise<AtlasCampaign[]> => {
  if (!isAtlasConfigured()) {
    return [];
  }

  try {
    const response = await atlasRequest<{ data: AtlasCampaign[] } | AtlasCampaign[]>('/campaigns');
    // Handle different response formats
    if (Array.isArray(response)) {
      return response;
    }
    return response.data || [];
  } catch (error) {
    console.error('Failed to fetch Atlas campaigns:', error);
    return [];
  }
};

/**
 * Get a single campaign by ID
 */
export const getCampaign = async (campaignId: string): Promise<AtlasCampaign | null> => {
  if (!isAtlasConfigured()) {
    return null;
  }

  try {
    const response = await atlasRequest<{ campaign: AtlasCampaign }>(`/campaigns/${campaignId}`);
    return response.campaign;
  } catch (error) {
    console.error(`Failed to fetch campaign ${campaignId}:`, error);
    return null;
  }
};

/**
 * Start a campaign
 */
export const startCampaign = async (campaignId: string): Promise<boolean> => {
  if (!isAtlasConfigured()) {
    return false;
  }

  try {
    await atlasRequest(`/campaigns/${campaignId}/start`, {
      method: 'POST',
    });
    return true;
  } catch (error) {
    console.error(`Failed to start campaign ${campaignId}:`, error);
    return false;
  }
};

/**
 * Pause a campaign
 */
export const pauseCampaign = async (campaignId: string): Promise<boolean> => {
  if (!isAtlasConfigured()) {
    return false;
  }

  try {
    await atlasRequest(`/campaigns/${campaignId}/pause`, {
      method: 'POST',
    });
    return true;
  } catch (error) {
    console.error(`Failed to pause campaign ${campaignId}:`, error);
    return false;
  }
};

// ============================================
// Agents API
// ============================================

/**
 * Get all agents
 */
export const getAgents = async (): Promise<AtlasAgent[]> => {
  if (!isAtlasConfigured()) {
    return [];
  }

  try {
    const response = await atlasRequest<{ agents: AtlasAgent[] }>('/agents');
    return response.agents || [];
  } catch (error) {
    console.error('Failed to fetch Atlas agents:', error);
    return [];
  }
};

// ============================================
// Actions API
// ============================================

/**
 * Get all actions
 */
export const getActions = async (): Promise<AtlasAction[]> => {
  if (!isAtlasConfigured()) {
    return [];
  }

  try {
    const response = await atlasRequest<{ actions: AtlasAction[] }>('/actions');
    return response.actions || [];
  } catch (error) {
    console.error('Failed to fetch Atlas actions:', error);
    return [];
  }
};

/**
 * Trigger an action manually
 */
export const triggerAction = async (
  actionId: string,
  payload?: Record<string, unknown>
): Promise<{ success: boolean; response?: unknown }> => {
  if (!isAtlasConfigured()) {
    return { success: false };
  }

  try {
    const response = await atlasRequest<{ success: boolean; response?: unknown }>(
      `/actions/${actionId}/trigger`,
      {
        method: 'POST',
        body: payload ? JSON.stringify(payload) : undefined,
      }
    );
    return response;
  } catch (error) {
    console.error(`Failed to trigger action ${actionId}:`, error);
    return { success: false };
  }
};

// ============================================
// Webhook Events API
// ============================================

/**
 * Get recent webhook events
 */
export const getWebhookEvents = async (limit = 50): Promise<AtlasWebhookEvent[]> => {
  if (!isAtlasConfigured()) {
    return [];
  }

  try {
    const response = await atlasRequest<{ events: AtlasWebhookEvent[] }>(
      `/webhook-events?limit=${limit}`
    );
    return response.events || [];
  } catch (error) {
    console.error('Failed to fetch webhook events:', error);
    return [];
  }
};

// ============================================
// Stats API
// ============================================

/**
 * Get call statistics - overview for all campaigns
 */
export const getStats = async (): Promise<AtlasStats | null> => {
  if (!isAtlasConfigured()) {
    return null;
  }

  try {
    // Atlas uses /statistics/overview endpoint
    const response = await atlasRequest<AtlasStats | { data: AtlasStats }>('/statistics/overview');

    // Handle different response formats
    if ('data' in response) {
      return response.data;
    }
    return response;
  } catch (error) {
    console.error('Failed to fetch Atlas stats:', error);
    return null;
  }
};

// ============================================
// Customer Matching Functions
// ============================================

/**
 * Normalize phone number for comparison (remove formatting)
 */
const normalizePhone = (phone: string | null | undefined): string => {
  if (!phone) return '';
  return phone.replace(/\D/g, '').slice(-10); // Get last 10 digits
};

/**
 * Normalize name for comparison (lowercase, trim)
 */
const normalizeName = (name: string | null | undefined): string => {
  if (!name) return '';
  return name.toLowerCase().trim();
};

/**
 * Check if two names match (fuzzy matching)
 */
const namesMatch = (name1: string | null | undefined, name2: string | null | undefined): boolean => {
  const n1 = normalizeName(name1);
  const n2 = normalizeName(name2);
  if (!n1 || !n2) return false;

  // Exact match
  if (n1 === n2) return true;

  // One name contains the other (handles "John" matching "John Smith")
  if (n1.includes(n2) || n2.includes(n1)) return true;

  // Check if first names match (handles "John Smith" vs "John Doe")
  const firstName1 = n1.split(' ')[0];
  const firstName2 = n2.split(' ')[0];
  if (firstName1 && firstName2 && firstName1 === firstName2) return true;

  return false;
};

/**
 * Find Atlas calls that match a customer by phone number or name
 */
export const findCallsForCustomer = async (customer: {
  phone?: string | null;
  alternatePhone?: string | null;
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}): Promise<AtlasCall[]> => {
  if (!isAtlasConfigured()) {
    return [];
  }

  try {
    const { calls } = await getCalls({ limit: 100 });

    const customerPhone = normalizePhone(customer.phone);
    const customerAltPhone = normalizePhone(customer.alternatePhone);
    const customerName = customer.name || `${customer.firstName || ''} ${customer.lastName || ''}`.trim();

    // Filter calls that match by phone or name
    const matchingCalls = calls.filter((call) => {
      const callPhone = normalizePhone(call.phoneNumber);

      // Phone match (primary check)
      if (customerPhone && callPhone === customerPhone) return true;
      if (customerAltPhone && callPhone === customerAltPhone) return true;

      // Name match (secondary check)
      if (namesMatch(call.customerName, customerName)) return true;

      return false;
    });

    // Sort by date (most recent first)
    return matchingCalls.sort((a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  } catch (error) {
    console.error('Failed to find calls for customer:', error);
    return [];
  }
};

/**
 * Get the most recent completed call with a recording for a customer
 */
export const getLatestRecordingForCustomer = async (customer: {
  phone?: string | null;
  alternatePhone?: string | null;
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}): Promise<AtlasCall | null> => {
  const calls = await findCallsForCustomer(customer);

  // Find the most recent completed call with a recording
  const callWithRecording = calls.find(
    (call) => call.status === 'completed' && call.recordingUrl
  );

  return callWithRecording || null;
};

// ============================================
// Utility Functions
// ============================================

/**
 * Format call duration to human readable
 */
export const formatDuration = (seconds: number | null): string => {
  if (seconds === null || seconds === 0) return '-';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins === 0) return `${secs}s`;
  return `${mins}m ${secs}s`;
};

/**
 * Get status color for UI
 */
export const getStatusColor = (
  status: string
): string => {
  const colors: Record<string, string> = {
    completed: '#10B981',
    in_progress: '#3B82F6',
    pending: '#F59E0B',
    failed: '#EF4444',
    no_answer: '#6B7280',
    active: '#10B981',
    paused: '#F59E0B',
    draft: '#6B7280',
  };
  return colors[status] || '#6B7280';
};

/**
 * Get sentiment color for UI
 */
export const getSentimentColor = (
  sentiment: string | null
): string => {
  const colors: Record<string, string> = {
    positive: '#10B981',
    neutral: '#F59E0B',
    negative: '#EF4444',
  };
  return sentiment ? colors[sentiment] || '#6B7280' : '#6B7280';
};

// ============================================
// Export API object
// ============================================

export const atlasApi = {
  // Configuration
  isConfigured: isAtlasConfigured,
  getConfigStatus: getAtlasConfigStatus,

  // Calls
  getCalls,
  getCall,
  scheduleCall,
  cancelCall,

  // Conversations
  getConversation,

  // Campaigns
  getCampaigns,
  getCampaign,
  startCampaign,
  pauseCampaign,

  // Agents
  getAgents,

  // Actions
  getActions,
  triggerAction,

  // Webhook Events
  getWebhookEvents,

  // Stats
  getStats,

  // Customer Matching
  findCallsForCustomer,
  getLatestRecordingForCustomer,

  // Utilities
  formatDuration,
  getStatusColor,
  getSentimentColor,
};

export default atlasApi;
